import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { EditTransactionModal, DeleteConfirmPopup } from '../components/TransactionModals';
import { ShoppingBag, Scissors, ArrowDownCircle, Save, Loader2 } from 'lucide-react';
import { useToast } from './Toast';
import { getPlaceholders } from '../utils/placeholders';

const TransactionManager = ({ employees, salonId, salonDevise, onTransactionSuccess, prefillData }) => {
  const showToast = useToast();
  const [activeForm, setActiveForm] = useState('prestation');
  const [loading, setLoading] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [txToDelete, setTxToDelete] = useState(null);

  const [formData, setFormData] = useState({
    employeeId: '', clientNom: '', montant: '', description: '',
    methodePaiement: 'Especes', nomProduit: '', quantite: 1, prixUnitaire: ''
  });

  useEffect(() => {
    if (prefillData) {
      setActiveForm('prestation');
      setFormData(prev => ({
        ...prev,
        employeeId:  prefillData.employeeId || '',
        clientNom:   prefillData.clientNom || '',
        description: prefillData.service || 'Prestation',
      }));
    }
  }, [prefillData]);

  const getCurrencyDisplay = () => {
    if (salonDevise === 'XOF') return 'FCFA';
    if (salonDevise === 'EUR') return '€';
    if (salonDevise === 'USD') return '$';
    return salonDevise;
  };

  const getPaymentOptions = () => {
    if (salonDevise === 'XOF') {
      return [
        { value: 'Especes',      label: 'Espèces',      icon: '💵',  logo: null },
        { value: 'Wave',         label: 'Wave',          icon: null,  logo: '/wave-logo.svg' },
        { value: 'Orange Money', label: 'Orange Money',  icon: null,  logo: '/orange-money-logo.svg' },
      ];
    }
    return [
      { value: 'Especes', label: 'Espèces',       icon: '💵', logo: null },
      { value: 'Carte',   label: 'Carte Bancaire', icon: '💳', logo: null },
    ];
  };

  const handleUpdate = async (id, data) => {
    try {
      await api.put(`/admin/transactions/${id}`, data);
      showToast("Transaction corrigée !");
      setSelectedTx(null);
      if (onTransactionSuccess) onTransactionSuccess();
    } catch (err) {
      showToast(err.response?.data?.message || "Erreur", "error");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/transactions/${txToDelete}`);
      showToast("Transaction supprimée !");
      setTxToDelete(null);
      if (onTransactionSuccess) onTransactionSuccess();
    } catch (err) {
      showToast("Erreur lors de la suppression", "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (activeForm !== 'produit' && formData.montant === '') {
      showToast('Veuillez saisir un montant.', 'error');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let payload = { salonId, methodePaiement: formData.methodePaiement };

      if (activeForm === 'prestation') {
        payload = {
          ...payload, type: 'Prestation',
          categorie:   prefillData?.service || 'Prestation',
          employeeId:  formData.employeeId,
          montant:     Number(formData.montant),
          clientNom:   formData.clientNom?.trim() || '',
          description: formData.description,
        };
      } else if (activeForm === 'depense') {
        payload = {
          ...payload, type: 'Depense', categorie: 'Frais',
          montant:     Number(formData.montant),
          description: formData.description,
        };
      } else if (activeForm === 'produit') {
        const total = Number(formData.prixUnitaire) * formData.quantite;
        payload = {
          ...payload, type: 'Vente', categorie: 'Produit',
          montant:     total,
          clientNom:   formData.clientNom?.trim() || '',
          description: `${formData.nomProduit} × ${formData.quantite}`,
        };
      }

      await api.post('/admin/transactions', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      showToast("Opération enregistrée avec succès !");
      setFormData({
        employeeId: '', clientNom: '', montant: '', description: '',
        methodePaiement: 'Especes', nomProduit: '', quantite: 1, prixUnitaire: ''
      });
      if (onTransactionSuccess) onTransactionSuccess();
    } catch (err) {
      showToast("Erreur : " + (err.response?.data?.message || err.message), "error");
    } finally {
      setLoading(false);
    }
  };

  const ph = getPlaceholders(salonDevise);
  const currency = getCurrencyDisplay();
  const paymentOptions = getPaymentOptions();

  const productTotal = activeForm === 'produit' && formData.prixUnitaire
    ? Number(formData.prixUnitaire) * Number(formData.quantite || 1)
    : null;

  const TYPES = [
    { id: 'prestation', label: 'Prestation', icon: Scissors,       accent: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE' },
    { id: 'produit',    label: 'Produit',    icon: ShoppingBag,    accent: '#059669', bg: '#ECFDF5', border: '#A7F3D0' },
    { id: 'depense',    label: 'Dépense',    icon: ArrowDownCircle, accent: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
  ];
  const activeType = TYPES.find(t => t.id === activeForm);

  const fmtAmount = (n) => Number(n || 0).toLocaleString('fr-FR');

  return (
    <>
      <div className="space-y-4 animate-in fade-in duration-300">

        {/* ── Sélecteur de type ── */}
        <div className="grid grid-cols-3 gap-3">
          {TYPES.map(t => {
            const Icon = t.icon;
            const isActive = activeForm === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveForm(t.id)}
                className="relative flex flex-col items-center gap-2 p-4 rounded-[1.5rem] border-2 transition-all duration-200 font-bold text-xs sm:text-sm"
                style={{
                  background:   isActive ? t.bg    : '#fff',
                  borderColor:  isActive ? t.accent : '#E2E8F0',
                  color:        isActive ? t.accent : '#94A3B8',
                  boxShadow:    isActive ? `0 4px 20px ${t.accent}22` : '0 1px 4px rgba(0,0,0,0.04)',
                  transform:    isActive ? 'translateY(-2px)' : 'translateY(0)',
                }}
              >
                <Icon size={20} strokeWidth={isActive ? 2.5 : 1.8} />
                <span className="hidden sm:inline">{t.label}</span>
                <span className="sm:hidden">{t.label.slice(0, 5)}</span>
                {isActive && (
                  <span
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 rotate-45"
                    style={{ background: t.accent, borderRadius: '2px' }}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* ── Formulaire ── */}
        <div
          className="rounded-[2rem] border-2 overflow-hidden transition-all duration-200"
          style={{ borderColor: activeType?.border, background: '#fff' }}
        >
          {/* Bandeau couleur en tête */}
          <div
            className="px-6 py-4 flex items-center gap-3"
            style={{ background: activeType?.bg, borderBottom: `1px solid ${activeType?.border}` }}
          >
            {React.createElement(activeType?.icon, {
              size: 18,
              style: { color: activeType?.accent },
              strokeWidth: 2.5
            })}
            <span className="font-black text-sm" style={{ color: activeType?.accent }}>
              {activeType?.label}
            </span>
          </div>

          <form onSubmit={handleSubmit} className="p-5 md:p-7 space-y-5">

            {/* PRESTATION : employé + client */}
            {activeForm === 'prestation' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Employé *</label>
                  <select required
                    className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none font-bold text-slate-700 text-sm border border-transparent focus:border-blue-300 transition-colors"
                    value={formData.employeeId}
                    onChange={e => setFormData({ ...formData, employeeId: e.target.value })}>
                    <option value="">Sélectionner...</option>
                    {employees.map(emp => (
                      <option key={emp._id} value={emp._id}>{emp.prenom}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Client</label>
                  <input
                    className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none font-medium text-sm border border-transparent focus:border-blue-300 transition-colors"
                    placeholder={ph.nomComplet}
                    value={formData.clientNom}
                    onChange={e => setFormData({ ...formData, clientNom: e.target.value })} />
                </div>
              </div>
            )}

            {/* PRODUIT : nom + prix + quantité + client */}
            {activeForm === 'produit' && (
              <>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Nom du produit *</label>
                  <input required
                    className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none font-medium text-sm border border-transparent focus:border-emerald-300 transition-colors"
                    placeholder="Ex : Gel coiffant 250ml"
                    value={formData.nomProduit}
                    onChange={e => setFormData({ ...formData, nomProduit: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Prix unitaire ({currency}) *</label>
                    <input required type="number" min="0"
                      className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none font-bold text-slate-800 text-lg border border-transparent focus:border-emerald-300 transition-colors"
                      placeholder="0"
                      value={formData.prixUnitaire}
                      onChange={e => setFormData({ ...formData, prixUnitaire: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Quantité *</label>
                    <input required type="number" min="1"
                      className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none font-bold text-slate-800 text-lg border border-transparent focus:border-emerald-300 transition-colors"
                      value={formData.quantite}
                      onChange={e => setFormData({ ...formData, quantite: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Client</label>
                  <input
                    className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none font-medium text-sm border border-transparent focus:border-emerald-300 transition-colors"
                    placeholder={ph.nomComplet}
                    value={formData.clientNom}
                    onChange={e => setFormData({ ...formData, clientNom: e.target.value })} />
                </div>
              </>
            )}

            {/* MONTANT (prestation + dépense) — grand écran caisse */}
            {activeForm !== 'produit' && (
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
                  Montant ({currency}) *
                </label>
                <div className="relative">
                  <input type="number" min="0"
                    className="w-full p-4 bg-slate-900 text-white rounded-2xl outline-none font-black text-3xl tracking-tight placeholder-slate-600"
                    style={{ letterSpacing: '-0.02em' }}
                    placeholder="0"
                    value={formData.montant}
                    onChange={e => setFormData({ ...formData, montant: e.target.value })} />
                  {formData.montant !== '' && Number(formData.montant) === 0 ? (
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-black uppercase tracking-widest px-2 py-1 rounded-lg bg-emerald-500 text-white">
                      Gratuit
                    </span>
                  ) : formData.montant ? (
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-black text-slate-400 uppercase tracking-widest">
                      {currency}
                    </span>
                  ) : null}
                </div>
              </div>
            )}

            {/* Total produit — écran de caisse */}
            {activeForm === 'produit' && (
              <div
                className="rounded-2xl p-4 flex items-center justify-between"
                style={{ background: productTotal ? '#ECFDF5' : '#F8FAFC', border: `1.5px solid ${productTotal ? '#A7F3D0' : '#E2E8F0'}` }}
              >
                <span className="text-sm font-bold text-slate-500">Total à encaisser</span>
                <span
                  className="font-black text-2xl tracking-tight"
                  style={{ color: productTotal ? '#059669' : '#CBD5E1', letterSpacing: '-0.02em' }}
                >
                  {productTotal ? fmtAmount(productTotal) : '0'}
                  <span className="text-sm font-bold ml-1.5" style={{ color: productTotal ? '#6EE7B7' : '#CBD5E1' }}>
                    {currency}
                  </span>
                </span>
              </div>
            )}

            {/* Méthode de paiement — chips cliquables */}
            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2.5">Paiement</label>
              <div className="flex flex-wrap gap-2">
                {paymentOptions.map(opt => {
                  const isSelected = formData.methodePaiement === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, methodePaiement: opt.value })}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-all border-2"
                      style={{
                        background:   isSelected ? activeType?.bg      : '#F8FAFC',
                        borderColor:  isSelected ? activeType?.accent   : '#E2E8F0',
                        color:        isSelected ? activeType?.accent   : '#64748B',
                        boxShadow:    isSelected ? `0 2px 10px ${activeType?.accent}20` : 'none',
                      }}
                    >
                      {opt.logo
                        ? <img src={opt.logo} alt={opt.label} style={{ height: '18px', borderRadius: '4px', objectFit: 'contain' }} />
                        : <span>{opt.icon}</span>
                      }
                      {!opt.logo && <span>{opt.label}</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
                Détails {activeForm === 'depense' && <span style={{ color: activeType?.accent }}>*</span>}
              </label>
              <textarea
                className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none font-medium h-20 text-sm resize-none border border-transparent transition-colors"
                style={{ '--tw-ring-color': activeType?.border }}
                placeholder={activeForm === 'depense' ? "Précisez la nature de la dépense" : "Notes additionnelles..."}
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                required={activeForm === 'depense'}
              />
            </div>

            {/* Bouton valider */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-2xl font-black text-base flex items-center justify-center gap-2.5 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: loading ? '#94A3B8' : activeType?.accent,
                color: '#fff',
                boxShadow: loading ? 'none' : `0 8px 24px ${activeType?.accent}40`,
              }}
            >
              {loading
                ? <><Loader2 size={18} className="animate-spin" /> Enregistrement...</>
                : <><Save size={18} /> Valider l'opération</>
              }
            </button>

          </form>
        </div>
      </div>

      {selectedTx && (
        <EditTransactionModal
          transaction={selectedTx}
          currency={getCurrencyDisplay()}
          onClose={() => setSelectedTx(null)}
          onUpdate={handleUpdate}
        />
      )}
      {txToDelete && (
        <DeleteConfirmPopup
          onConfirm={handleDelete}
          onCancel={() => setTxToDelete(null)}
        />
      )}
    </>
  );
};

export default TransactionManager;
